import React, { useState, useMemo } from 'react';
import DefaultButton from 'components/Utils/NewDefaultButton';
import MiscUtils from 'services/MiscUtils';
import './style.scss';

//https://www.mdcalc.com/ciwa-ar-alcohol-withdrawal
const AlcoholCalculatorModal = ({ patient, onSubmit }) => {
  const [data, setData] = useState({});

  const points = useMemo(() => {
    let total = 0;
    QUESTIONS.forEach((question) => {
      total += data[question.title] || 0;
    });
    return total;
  }, [data]);

  function getResult() {
    return `Score of ${points} points on the CIWA-Ar Alcohol Withdrawal questionnaire`;
  }

  async function copyToCliboard() {
    let result = getResult();
    await navigator.clipboard.writeText(result);
    MiscUtils.showToastrInfo({
      title: 'Alcohol Calculator',
      message: 'Score copied successfully!',
    });
  }

  return (
    <div className="alcohol-calculator modal">
      <div className="title">CIWA-Ar for Alcohol Withdrawal</div>
      <div className="content">
        <div className="questions">
          {QUESTIONS.map(({ title, subtitle, answers }, index) => {
            return (
              <div className="question">
                {index > 0 ? <hr /> : null}
                <span className="small-title">{title}</span>
                {subtitle ? <span className="subtitle">{subtitle}</span> : null}
                <div className="answers">
                  {answers.map((text, index) => {
                    let className = 'answer';
                    if ((data[title] || 0) === index) className += ' selected';
                    return (
                      <div
                        className={className}
                        onClick={() => {
                          setData((prevState) => {
                            return {
                              ...prevState,
                              [title]: index,
                            };
                          });
                        }}
                      >
                        <span>{text}</span>
                        <span className="points">{index > 0 ? `+${index}` : index}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="results">
          <div className="row points">
            <span className="points-text">
              <b>{points}</b> points
            </span>
            <div className="button-wrapper">
              <DefaultButton
                icon={onSubmit ? undefined : 'copy'}
                name={onSubmit ? 'Submit' : 'Copy'}
                onClick={onSubmit ? () => onSubmit(getResult()) : copyToCliboard}
                style={{ width: '100px' }}
              />
            </div>
          </div>
          <div className="row">
            Patients with scores ≤8 typically do not require medication for withdrawal.
          </div>
        </div>
      </div>
    </div>
  );
};

const QUESTIONS = [
  {
    title: 'Nausea/vomiting',
    subtitle: 'Ask "Do you feek sick to your stomach? Have you vomited?"',
    answers: [
      'No nausea and no vomiting',
      'Mild nausea and no vomiting',
      '(More severe symptoms)',
      '(More severe symptoms)',
      'Intermittent nausea with dry heaves',
      '(More severe symptoms)',
      '(More severe symptoms)',
      'Constant nausea, frequent dry heaves and vomiting',
    ],
  },
  {
    title: 'Tremor',
    subtitle: 'Arms extended and fingers spread apart',
    answers: [
      'No tremor',
      'Not visible, but can be felt fingertip to fingertip',
      '(More severe symptoms)',
      '(More severe symptoms)',
      "Moderate, with patient's arms extended",
      '(More severe symptoms)',
      '(More severe symptoms)',
      'Severe, even with arms not extended',
    ],
  },
  {
    title: 'Paroxysmal sweats',
    answers: [
      'No sweat visible',
      'Barely perceptible sweating, palms moist',
      '(More severe symptoms)',
      '(More severe symptoms)',
      'Beads of sweat obvious on forehead',
      '(More severe symptoms)',
      '(More severe symptoms)',
      'Drenching sweats',
    ],
  },
  {
    title: 'Anxiety',
    subtitle: 'Ask "Do you feel nervous?"',
    answers: [
      'No anxiety, at ease',
      'Mildly anxious',
      '(More severe symptoms)',
      '(More severe symptoms)',
      'Moderately anxious, or guarded, so anxiety is inferred',
      '(More severe symptoms)',
      '(More severe symptoms)',
      'Equivalent to acute panic states as seen in severe delirium or acute schizophrenic reactions',
    ],
  },
  {
    title: 'Agitation',
    answers: [
      'Normal activity',
      'Somewhat more activity than normal activty',
      '(More severe symptoms)',
      '(More severe symptoms)',
      'Moderately fidgety and restless',
      '(More severe symptoms)',
      '(More severe symptoms)',
      'Paces back and forth during most of the interview, or constantly thrashes about',
    ],
  },
  {
    title: 'Tactile disturbances',
    subtitle:
      'Ask, "Have you any itching, pins and needles sensations, any burning, any numbness, or do you feel bugs crawling on or under your skin?"',
    answers: [
      'None',
      'Very mild itching, pin and needles, burning, or numbness',
      'Mild itching, pin and needles, burning, or numbness',
      'Moderate itching, pin and needles, burning, or numbness',
      'Moderately severe hallucinations',
      'Severe hallucinations',
      'Extremely severe hallucinations',
      'Continuous hallucinations',
    ],
  },
  {
    title: 'Auditory disturbances',
    subtitle:
      'Ask, "Are you more aware of sounds around you? Are they harsh? Do they frighten you? Are you hearing anything that is disturbing to you? Are you hearing things you know are not there?"',
    answers: [
      'Not present',
      'Very mild harshness or ability to frighten',
      'Mild harshness or ability to frighten',
      'Moderate harshness or ability to frighten',
      'Moderately severe hallucinations',
      'Severe hallucinations',
      'Extremely severe hallucinations',
      'Continuous hallucinations',
    ],
  },
  {
    title: 'Visual disturbances',
    subtitle:
      'Ask "Does the light appear to be too bright? Is its color different? Does it hurt your eyes? Are you seeing anything that is disturbing to you? Are you seeing things you know are not there?"',
    answers: [
      'Not present',
      'Very mild sensitivity',
      'Mild sensitivity',
      'Moderate sensitivity',
      'Moderately severe hallucinations',
      'Severe hallucinations',
      'Extremely severe hallucinations',
      'Continuous hallucinations',
    ],
  },
  {
    title: 'Headache/fullness in head',
    subtitle:
      'Ask "Does your head feel different? Does it feel like there is a band around your head?" Do not rate for dizziness or lightheadedness. Otherwise, rate "severity".',
    answers: [
      'Not Present',
      'Very mild',
      'Mild',
      'Moderate',
      'Moderately severe',
      'Severe',
      'Very severe',
      'Extremely severe',
    ],
  },
  {
    title: 'Orientation/clouding of sensorium',
    subtitle: 'Ask "What day is this? Where are you? Who am I?"',
    answers: [
      'Oriented, can do serial additions',
      "Can't do serial additions or is uncertain about date",
      'Disoriented for date by no more than 2 calendar days',
      'Disoriented for date by more than 2 calendar days',
      'Disoriented to place or person',
    ],
  },
];

export default AlcoholCalculatorModal;
